import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CoreContext } from "@scmlatam/web-ui";
import type { useStepper } from "./useStepper";

const useStepResume = (props: ReturnType<typeof useStepper>) => {
  const { t, categories, selectedIds, rowSelected, rows, range } = props;
  const { format } = useContext(CoreContext);
  const categoryItems = categories ?? [];

  const selectedIdSet = useMemo(
    () => new Set(selectedIds.map((id) => String(id))),
    [selectedIds],
  );

  const selectedReports = useMemo(() => {
    return categoryItems
      .flatMap((category) => category.children ?? [])
      .filter((category) => selectedIdSet.has(String(category.id)))
      .map((category) => category.label)
      .filter((label): label is string => typeof label === "string");
  }, [categoryItems, selectedIdSet]);

  const selectedWorkers = useMemo(() => {
    if (Array.isArray(rowSelected)) {
      const selectedRows = new Set(rowSelected.map((id) => String(id)));
      return rows.filter((row) => selectedRows.has(String(row.id)));
    }

    if (rowSelected instanceof Set) {
      const selectedRows = new Set(Array.from(rowSelected).map(String));
      return rows.filter((row) => selectedRows.has(String(row.id)));
    }

    const rowSelectionIds = rowSelected.ids;
    const selectedRows = new Set(
      Array.isArray(rowSelectionIds)
        ? rowSelectionIds.map(String)
        : Array.from(rowSelectionIds ?? []).map(String),
    );

    return rows.filter((row) => {
      const isSelected = selectedRows.has(String(row.id));
      return rowSelected.type === "exclude" ? !isSelected : isSelected;
    });
  }, [rowSelected, rows]);

  const workersSignature = useMemo(
    () => selectedWorkers.map((worker) => worker.id).join("|"),
    [selectedWorkers],
  );

  const reportsSignature = useMemo(
    () => selectedReports.join("|"),
    [selectedReports],
  );

  const startFormatted =
    range[0]?.isValid() && format
      ? range[0].format(format)
      : t("step4.resume.empty_date", "00/00/0000");

  const endFormatted =
    range[1]?.isValid() && format
      ? range[1].format(format)
      : t("step4.resume.empty_date", "00/00/0000");

  const workersContainerRef = useRef<HTMLDivElement | null>(null);
  const workersListRef = useRef<HTMLUListElement | null>(null);
  const reportsContainerRef = useRef<HTMLDivElement | null>(null);
  const reportsListRef = useRef<HTMLUListElement | null>(null);

  const [visibleWorkersCount, setVisibleWorkersCount] = useState<number>(
    selectedWorkers.length,
  );
  const [isWorkersListTruncated, setIsWorkersListTruncated] =
    useState<boolean>(false);

  const [visibleReportsCount, setVisibleReportsCount] = useState<number>(
    selectedReports.length,
  );
  const [isReportsListTruncated, setIsReportsListTruncated] =
    useState<boolean>(false);

  const previousWorkersSignatureRef = useRef<string>("");
  const previousReportsSignatureRef = useRef<string>("");

  useEffect(() => {
    const calculateVisibleItems = ({
      container,
      list,
      totalItems,
    }: {
      container: HTMLDivElement | null;
      list: HTMLUListElement | null;
      totalItems: number;
    }) => {
      if (!container || !list) {
        return { nextVisibleCount: totalItems, isTruncated: false };
      }

      if (list.scrollHeight <= container.clientHeight) {
        return { nextVisibleCount: totalItems, isTruncated: false };
      }

      const listItems = Array.from(list.querySelectorAll("li"));
      const containerHeight = container.clientHeight;

      if (listItems.length === 0 || containerHeight === 0) {
        return { nextVisibleCount: totalItems, isTruncated: false };
      }

      const firstStyles = window.getComputedStyle(listItems[0]);
      const ellipsisHeight =
        listItems[0].getBoundingClientRect().height +
        parseFloat(firstStyles.marginBottom || "0");

      let totalHeight = 0;
      let nextVisibleCount = 0;

      for (let index = 0; index < listItems.length; index += 1) {
        const currentStyles = window.getComputedStyle(listItems[index]);
        const currentHeight =
          listItems[index].getBoundingClientRect().height +
          parseFloat(currentStyles.marginBottom || "0");

        const reserveEllipsis =
          index < listItems.length - 1 ? ellipsisHeight : 0;

        if (totalHeight + currentHeight + reserveEllipsis > containerHeight) {
          break;
        }

        totalHeight += currentHeight;
        nextVisibleCount += 1;
      }

      return {
        nextVisibleCount: Math.max(0, nextVisibleCount),
        isTruncated: nextVisibleCount < listItems.length,
      };
    };

    const workersChanged =
      previousWorkersSignatureRef.current !== workersSignature;
    const reportsChanged =
      previousReportsSignatureRef.current !== reportsSignature;

    previousWorkersSignatureRef.current = workersSignature;
    previousReportsSignatureRef.current = reportsSignature;

    let shouldWaitForFullRender = false;

    if (workersChanged) {
      if (visibleWorkersCount !== selectedWorkers.length) {
        setVisibleWorkersCount(selectedWorkers.length);
        shouldWaitForFullRender = true;
      }

      if (isWorkersListTruncated) {
        setIsWorkersListTruncated(false);
        shouldWaitForFullRender = true;
      }
    }

    if (reportsChanged) {
      if (visibleReportsCount !== selectedReports.length) {
        setVisibleReportsCount(selectedReports.length);
        shouldWaitForFullRender = true;
      }

      if (isReportsListTruncated) {
        setIsReportsListTruncated(false);
        shouldWaitForFullRender = true;
      }
    }

    if (shouldWaitForFullRender) {
      return;
    }

    const shouldMeasureWorkers =
      visibleWorkersCount === selectedWorkers.length && !isWorkersListTruncated;
    const shouldMeasureReports =
      visibleReportsCount === selectedReports.length && !isReportsListTruncated;

    if (!shouldMeasureWorkers && !shouldMeasureReports) {
      return;
    }

    const workersResult = shouldMeasureWorkers
      ? calculateVisibleItems({
          container: workersContainerRef.current,
          list: workersListRef.current,
          totalItems: selectedWorkers.length,
        })
      : {
          nextVisibleCount: visibleWorkersCount,
          isTruncated: isWorkersListTruncated,
        };

    const reportsResult = shouldMeasureReports
      ? calculateVisibleItems({
          container: reportsContainerRef.current,
          list: reportsListRef.current,
          totalItems: selectedReports.length,
        })
      : {
          nextVisibleCount: visibleReportsCount,
          isTruncated: isReportsListTruncated,
        };

    if (workersResult.nextVisibleCount !== visibleWorkersCount) {
      setVisibleWorkersCount(workersResult.nextVisibleCount);
    }

    if (workersResult.isTruncated !== isWorkersListTruncated) {
      setIsWorkersListTruncated(workersResult.isTruncated);
    }

    if (reportsResult.nextVisibleCount !== visibleReportsCount) {
      setVisibleReportsCount(reportsResult.nextVisibleCount);
    }

    if (reportsResult.isTruncated !== isReportsListTruncated) {
      setIsReportsListTruncated(reportsResult.isTruncated);
    }
  }, [
    selectedWorkers,
    selectedReports,
    workersSignature,
    reportsSignature,
    visibleWorkersCount,
    isWorkersListTruncated,
    visibleReportsCount,
    isReportsListTruncated,
  ]);

  return {
    selectedReports,
    selectedWorkers,
    startFormatted,
    endFormatted,
    workersContainerRef,
    workersListRef,
    reportsContainerRef,
    reportsListRef,
    visibleWorkersCount,
    isWorkersListTruncated,
    visibleReportsCount,
    isReportsListTruncated,
  };
};

export { useStepResume };
